import { localStorage } from '@/infra/storage'
import { memoize } from '@/utils/memoize-util'
import { ValueRetrievablePromise } from '@/utils/value-retrievable-promise'
import type { Id } from '@/infra/id'
import type {
  AdvisorClientsTabViewModelFactory,
  ClientAssignmentViewModelFactory,
  ClientUserManagementFeatureOptions,
} from '@/features/client-user-management'
import { ClientUserManagementViewModel } from '@/features/client-user-management/ClientUserManagement/ClientUserManagementViewModel'
import { ClientUserDetailsViewModel } from '@/features/client-user-management/ClientUserDetails/ClientUserDetailsViewModel'
import { AdvisorClientsTabViewModel } from '@/features/client-user-management/ClientUserDetails/AdvisorClientsTabViewModel'
import { ClientAssignmentViewModel } from '@/features/client-user-management/ClientAssignmentViewModel'

/**
 * Function to acquire the view model for a client's details page.
 */
export type AcquireClientUserDetailsViewModelFn = (
  userPublicId: Id,
  signal?: AbortSignal,
) => Promise<ClientUserDetailsViewModel>

export function InternalClientUserManagementFeature(
  options: ClientUserManagementFeatureOptions,
) {
  const clientUserManagementViewModel = new ClientUserManagementViewModel(
    options.getCurrentMember,
    localStorage,
    options.searchUsers,
    options.searchMembersByPermission,
    options.createMemberManually,
    options.createCustomer,
    options.bulkCreateCustomers,
    options.newAlert,
    options.createAlertErrorHandler,
    options.createAlertRetryWrapper,
  )

  const createAdvisorClientsTabViewModel: AdvisorClientsTabViewModelFactory = ({
    getAdvisorUserPublicId,
  }) =>
    new AdvisorClientsTabViewModel(
      getAdvisorUserPublicId,
      options.getCurrentMember,
      localStorage,
      options.searchUsers,
      options.createAlertRetryWrapper,
    )

  const createClientAssignmentViewModel: ClientAssignmentViewModelFactory = ({
    getUserPublicId,
    canHaveAssignment,
  }) =>
    new ClientAssignmentViewModel(
      getUserPublicId,
      canHaveAssignment,
      options.getCurrentMember,
      options.fetchMember,
      options.fetchMembersByPublicIds,
      options.fetchClientAssignment,
      options.createAlertRetryWrapper,
    )

  const acquireClientUserDetailsViewModel: AcquireClientUserDetailsViewModelFn =
    memoize(
      async (userPublicId: Id, signal?: AbortSignal) => {
        signal?.throwIfAborted()

        const { createUserJobsTabViewModel, createDocumentsTabViewModel } =
          await ValueRetrievablePromise.props({
            createUserJobsTabViewModel: options.createUserJobsTabViewModel,
            createDocumentsTabViewModel: options.createDocumentsTabViewModel,
          })

        return new ClientUserDetailsViewModel(
          userPublicId,
          options.getCurrentMember,
          options.fetchMember,
          () => clientUserManagementViewModel.canAccessClientUserManagement,
          options.promptCreateJob,
          createUserJobsTabViewModel,
          createDocumentsTabViewModel,
          createAdvisorClientsTabViewModel,
          createClientAssignmentViewModel,
        )
      },
      {
        // Only the open client's page state is kept, so a revalidating route
        // reuses it while switching clients starts afresh.
        maxCount: 1,
        promise: ([, signal]) => signal,
      },
    )

  return {
    // Internal
    clientUserManagementViewModel,
    acquireClientUserDetailsViewModel,
    createClientAssignmentViewModel,
    createAdvisorClientsTabViewModel,
  }
}
